import { useCallback, useEffect, useMemo, useState } from 'react';
import { SolanaWalletAdapter } from '../controller/AccountController/BaseAccountController';

type SolanaProvider = {
  isPhantom?: boolean;
  isConnected?: boolean;
  publicKey?: { toString: () => string };
  connect: () => Promise<{ publicKey?: { toString: () => string } } | void>;
  disconnect?: () => Promise<void>;
  on?: (event: string, handler: (...args: any[]) => void) => void;
  off?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
  signTransaction?: (tx: any) => Promise<any>;
  signAndSendTransaction?: (tx: any, options?: any) => Promise<any>;
};

declare global {
  interface Window {
    solana?: SolanaProvider;
  }
}

const getProvider = () =>
  typeof window !== 'undefined' ? window.solana : undefined;

export const useSolanaWallet = () => {
  const [provider, setProvider] = useState<SolanaProvider | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const solana = getProvider();
    console.log('solana', solana);

    if (!solana) return;
    setProvider(solana);
    if (solana.isConnected && solana.publicKey) {
      setPublicKey(solana.publicKey.toString());
      setIsConnected(true);
    }
  }, []);

  useEffect(() => {
    if (!provider?.on) return;
    const handleConnect = () => {
      const key = provider.publicKey?.toString();
      if (key) {
        setPublicKey(key);
      }
      setIsConnected(true);
    };
    const handleDisconnect = () => {
      setPublicKey(null);
      setIsConnected(false);
    };
    const handleAccountChanged = (nextKey: any) => {
      const key = nextKey?.toString?.() || provider.publicKey?.toString?.();
      setPublicKey(key || null);
      setIsConnected(!!key);
    };

    provider.on('connect', handleConnect);
    provider.on('disconnect', handleDisconnect);
    provider.on('accountChanged', handleAccountChanged);

    return () => {
      const off = provider.off || provider.removeListener;
      if (off) {
        off.call(provider, 'connect', handleConnect);
        off.call(provider, 'disconnect', handleDisconnect);
        off.call(provider, 'accountChanged', handleAccountChanged);
      }
    };
  }, [provider]);

  const connect = useCallback(async () => {
    if (!provider) {
      throw new Error('Solana wallet not found');
    }
    if (!provider.signTransaction) {
      throw new Error('Solana wallet does not support signing');
    }
    setIsConnecting(true);
    try {
      const result: any = await provider.connect();
      const key = result?.publicKey || provider.publicKey;
      const keyString = key?.toString?.() || null;
      setPublicKey(keyString);
      setIsConnected(!!keyString);
      return keyString;
    } finally {
      setIsConnecting(false);
    }
  }, [provider]);

  const disconnect = useCallback(async () => {
    if (!provider) {
      setPublicKey(null);
      setIsConnected(false);
      return;
    }
    try {
      if (provider.disconnect) {
        await provider.disconnect();
      }
    } finally {
      setPublicKey(null);
      setIsConnected(false);
    }
  }, [provider]);

  const walletAdapter = useMemo<SolanaWalletAdapter | null>(() => {
    if (!provider?.signTransaction) {
      return null;
    }
    return {
      signTransaction: (tx: any) => provider.signTransaction!(tx),
      signAndSendTransaction: provider.signAndSendTransaction
        ? (tx: any, options?: any) =>
          provider.signAndSendTransaction!(tx, options)
        : undefined,
    };
  }, [provider]);

  return {
    isAvailable: !!provider,
    isConnected,
    isConnecting,
    publicKey,
    connect,
    disconnect,
    walletAdapter,
  };
};
